import React, { useState, useRef } from 'react'
import {
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  CardActions,
} from '@mui/material'
import {
  CloudUpload,
  Delete,
  Image as ImageIcon,
  Close,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { getAbsoluteImageUrl, validateImageFile } from '../utils/imageUtils'

interface ImageUploadProps {
  label?: string
  value?: string
  onChange: (url: string) => void
  onUpload?: (file: File) => Promise<{ url: string }>
  maxSizeMB?: number
  recommendedSize?: string
  disabled?: boolean
  showPreview?: boolean
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  label = 'Upload Image',
  value,
  onChange,
  onUpload,
  maxSizeMB = 5,
  recommendedSize = '800x800px',
  disabled = false,
  showPreview = true,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState<string | null>(value || null)
  const [dragOver, setDragOver] = useState(false)

  React.useEffect(() => {
    setPreview(value || null)
  }, [value])

  const handleFileSelect = async (file: File) => {
    // Validate file
    const error = validateImageFile(file, maxSizeMB)
    if (error) {
      enqueueSnackbar(error, { variant: 'error' })
      return
    }

    // Upload file if onUpload handler provided
    if (onUpload) {
      setUploading(true)
      // Create temporary preview while uploading
      const tempPreview = URL.createObjectURL(file)
      setPreview(tempPreview)
      
      try {
        const result = await onUpload(file)
        // Update with actual uploaded URL
        setPreview(result.url)
        onChange(result.url)
        enqueueSnackbar('Image uploaded successfully!', { variant: 'success' })
      } catch (error: any) {
        console.error('Upload error:', error)
        enqueueSnackbar(
          error?.response?.data?.detail || 'Failed to upload image',
          { variant: 'error' }
        )
        setPreview(null)
        // Revoke the temporary preview
        URL.revokeObjectURL(tempPreview)
      } finally {
        setUploading(false)
      }
    } else {
      // If no upload handler, create local preview
      const reader = new FileReader()
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string
        setPreview(dataUrl)
        onChange(dataUrl)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
    // Reset input value to allow selecting the same file again
    event.target.value = ''
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = () => {
    setDragOver(false)
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setDragOver(false)

    const file = event.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleRemove = () => {
    setPreview(null)
    onChange('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClick = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click()
    }
  }

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileInputChange}
        disabled={disabled || uploading}
      />

      {showPreview && preview ? (
        // Preview Mode
        <Card
          sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <CardMedia
            component="img"
            image={getAbsoluteImageUrl(preview)}
            alt="Preview"
            sx={{
              width: '100%',
              height: 300,
              objectFit: 'contain',
              bgcolor: '#f5f5f5',
            }}
            onError={(e) => {
              console.error('Failed to load image:', preview)
            }}
          />
          <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
            <Button
              size="small"
              startIcon={<CloudUpload />}
              onClick={handleClick}
              disabled={disabled || uploading}
            >
              Change Image
            </Button>
            <IconButton
              size="small"
              color="error"
              onClick={handleRemove}
              disabled={disabled || uploading}
            >
              <Delete />
            </IconButton>
          </CardActions>
        </Card>
      ) : (
        // Upload Mode
        <Box
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : '#e0e0e0',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: disabled || uploading ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            bgcolor: dragOver ? 'primary.50' : 'transparent',
            opacity: disabled ? 0.6 : 1,
            '&:hover': {
              borderColor: disabled || uploading ? '#e0e0e0' : 'primary.main',
              bgcolor: disabled || uploading ? 'transparent' : 'rgba(33, 150, 243, 0.04)',
            },
          }}
        >
          {uploading ? (
            <Box>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Uploading image...
              </Typography>
            </Box>
          ) : (
            <Box>
              <CloudUpload
                sx={{
                  fontSize: 48,
                  color: 'primary.main',
                  mb: 2,
                }}
              />
              <Typography variant="h6" gutterBottom>
                {label}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Click to upload or drag and drop
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                PNG, JPG, WebP up to {maxSizeMB}MB
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Recommended: {recommendedSize}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ mt: 2 }}
                disabled={disabled}
              >
                Choose File
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default ImageUpload
